/**
 * src/state/actions/obligationEnable.js
 *
 * The one client path that enables an obligation (msr 4.1b, GH #7351).
 * obligations-enable stamps enabledAt / enabledBy on the server and records
 * the change in the isms-change ledger; state-write no longer takes a new
 * enablement from a whole-state save. Every enable button calls this, then
 * the local copy is set to exactly what the server stored — so the next
 * autosave carries the stored entry and nothing is rewritten.
 *
 * Disabling and marking out of scope stay local edits saved through
 * state-write.
 */

import { enableObligations } from '../../api/ismsWritersApi.js';

/** Apply an obligations-enable response to a state draft (updateState). */
export function applyObligationEnableResult(s, result) {
  const entries = Array.isArray(result?.entries)
    ? result.entries.filter((e) => e?.obligationId)
    : [];
  if (entries.length) {
    const list = Array.isArray(s.enabledObligations) ? [...s.enabledObligations] : [];
    for (const entry of entries) {
      const i = list.findIndex((e) => e?.obligationId === entry.obligationId);
      if (i >= 0) list[i] = entry;
      else list.push(entry);
    }
    s.enabledObligations = list;
  }
  const decisions =
    result?.decisions && typeof result.decisions === 'object' ? result.decisions : {};
  if (Object.keys(decisions).length) {
    if (!s.obligationDecisions) s.obligationDecisions = {};
    for (const [id, d] of Object.entries(decisions)) s.obligationDecisions[id] = d;
  }
  // GH #7429: the server-written owner task of the context change this enable
  // raised. The response's stateVersion includes it, so the next autosave
  // must carry it — or that save would drop the task.
  const tasks = Array.isArray(result?.tasks) ? result.tasks.filter((t) => t?.id) : [];
  if (tasks.length) {
    const list = Array.isArray(s.adhocTasks) ? [...s.adhocTasks] : [];
    for (const task of tasks) {
      const i = list.findIndex((t) => t?.id === task.id);
      if (i >= 0) list[i] = task;
      else list.push(task);
    }
    s.adhocTasks = list;
  }
}

/**
 * Enable obligations on the server and apply the result locally.
 * Throws when the server refuses or cannot be reached — nothing is applied
 * locally then, so the screen never shows an enablement that did not happen.
 *
 * @param {Function} updateState
 * @param {string[]} obligationIds
 * @param {{ assigneeId?: string|null, markInScope?: boolean }} [options]
 */
export async function enableObligationsNow(updateState, obligationIds, options = {}) {
  const result = await enableObligations(obligationIds, options);
  if (!result?.ok) throw new Error('The obligation was not enabled — please try again.');
  updateState((s) => applyObligationEnableResult(s, result));
  return result;
}
